import { useState } from "react";
import { useMasters } from "../../contexts/MastersContext";

const InterviewForm = ({
  closeModal,
  onSubmit,
  initialValues = {
    candidate_id: "",
    interviewer: "",
    scheduled_at: "",
    status: "Scheduled",
    interview_type: "", // Default interview type
    interview_link: "",
    location: "",
  },
}) => {
  const { candidates, interviewers } = useMasters(); // Use context for candidates and interviewers
  const [formData, setFormData] = useState(initialValues);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "interview_link" && value === "generate") {
      const generateRandomString = (length) => {
        const characters =
          "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        let result = "";
        for (let i = 0; i < length; i++) {
          result += characters.charAt(
            Math.floor(Math.random() * characters.length)
          );
        }
        return result;
      };

      const randomString = generateRandomString(8); // Generate a random string of length 8
      const autogeneratedLink = `https://teamtime.live/room/${randomString}`;

      setFormData((prev) => ({ ...prev, interview_link: autogeneratedLink }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (name === "scheduled_at") setError(""); // Clear error when user changes date
  };

  const isValidDate = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set today's time to 00:00:00

    const inputDate = new Date(dateString);

    return inputDate >= today;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isValidDate(formData.scheduled_at)) {
      setError("Scheduled date must be today or later.");
      return;
    }

    onSubmit(formData);
    closeModal();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 font-poppins">
      <div>
        <label
          htmlFor="candidate_id"
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          Candidate <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          name="candidate_id"
          id="candidate_id"
          value={formData.candidate_id}
          onChange={handleChange}
          className="mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md"
          required
        >
          <option value="">Select Candidate</option>
          {candidates.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="interviewer"
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          Interviewer <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          name="interviewer"
          id="interviewer"
          value={formData.interviewer}
          onChange={handleChange}
          className="mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md"
          required
        >
          <option value="">Select Interviewer</option>
          {interviewers.map((user) => (
            <option key={user.id} value={user.name}>
              {user.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label
          htmlFor="interview_type"
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          Interview Type <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          name="interview_type"
          id="interview_type"
          value={formData.interview_type}
          onChange={handleChange}
          className="mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md"
          required
        >
          <option value="">Select Type</option>
          <option value="virtual">Virtual</option>
          <option value="physical">Physical</option>
        </select>
      </div>

      {formData.interview_type === "virtual" && (
        <div>
          <label
            htmlFor="interview_link"
            className="block text-sm font-medium text-gray-700 dark:text-white"
          >
            Interview Link
          </label>
          <div className="flex items-center gap-4">
            <input
              type="text"
              name="interview_link"
              id="interview_link"
              value={formData.interview_link}
              onChange={handleChange}
              className="w-full block p-2 dark:bg-black dark:text-white dark:border-gray-700 border border-gray-300 rounded-md"
              placeholder="Enter or generate interview link"
            />
            <button
              type="button"
              onClick={() =>
                handleChange({
                  target: {
                    name: "interview_link",
                    value: "generate",
                  },
                })
              }
              className="w-full px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Generate Link
            </button>
          </div>
        </div>
      )}

      {formData.interview_type === "physical" && (
        <div>
          <label
            htmlFor="location"
            className="block text-sm font-medium text-gray-700 dark:text-white"
          >
            Location

            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            name="location"
            id="location"
            value={formData.location}
            onChange={handleChange}
            className="mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border border-gray-300 rounded-md"
            placeholder="Enter physical interview location"
            required
          />
        </div>
      )}

      <div>
        <label
          htmlFor="scheduled_at"
          className="block text-sm font-medium text-gray-700 dark:text-white"
        >
          Scheduled At <span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="datetime-local"
          name="scheduled_at"
          id="scheduled_at"
          value={formData.scheduled_at}
          onChange={handleChange}
          className={`mt-1 dark:bg-black dark:text-white dark:border-gray-700 block w-full p-2 border rounded-md ${
            error ? "border-red-500" : "border-gray-300"
          }`}
          required
        />
        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>

      <div className="flex justify-end gap-2 items-center">
        <button
          type="button"
          onClick={closeModal}
          className="px-12 py-2 font-medium text-black hover:bg-gray-200 dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-2 font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          {initialValues.id ? "Update Interview" : "Add Interview"}
        </button>
      </div>
    </form>
  );
};

export default InterviewForm;
